import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useWorkspaceReady } from '../../auth/workspace-entry-context';
import type { ResourceScope } from '@kop/contracts';
import { Button } from '@kop/ui';
import { RefreshCw, ArrowUpRight } from 'lucide-react';
import { HubHeader } from '../hub-shell/hub-shell';
import { HomeBriefing } from './home-briefing';
import type { SafetyBoardAuthority } from '../safety-board/model';
import { type WorkHomeSnapshot } from './work-home-projection';
import { DashboardCalendar } from './dashboard-calendar';
import { ActivityIndexPanel } from './activity-index-panel';
import { safetyProgramActivityReader } from '../safety-program/dashboard-projection';
import { loadDashboard, type DashboardRead } from './dashboard-read';
import { dashboardWorkRows, type DashboardWorkSelection } from './dashboard-work-model';
import { readDashboardManagement } from './dashboard-management-read';
import { TBM_DATA_CHANGED_EVENT } from '../../runtime/tbm-data-events';
import './operations-dashboard.css';
import './dashboard-compact.css';

const localDay = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
const number = (value: number | null | undefined) =>
  value == null ? '—' : value.toLocaleString('ko-KR', { maximumFractionDigits: 1 });
const authorizationFailure = (error: unknown) => {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code)
      : '';
  return ['unauthenticated', 'permission-denied', 'failed-precondition'].some(
    (value) => code === value || code === `functions/${value}`,
  );
};
function Panel({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="home-panel">
      <header>
        <h2>{title}</h2>
        {note && <small>{note}</small>}
      </header>
      {children}
    </section>
  );
}

export function OperationsDashboard({
  uid,
  scope,
  featureIds,
  onOpen,
  onInspection,
  onFindings,
  onWork,
  onReport,
  authority,
  owner,
  snapshot,
}: {
  uid: string;
  scope: ResourceScope;
  featureIds: readonly string[];
  onOpen: (id: string) => void;
  onInspection?: (period: { from: string; to: string } | null) => void;
  onFindings?: (selection: {period:{from:string;to:string};status?:'complete'|'not-completed'}) => void;
  onWork?: (selection: DashboardWorkSelection) => void;
  onReview?: () => void;
  onReport?: () => void;
  onOverdue?: () => void;
  authority: SafetyBoardAuthority;
  owner: { uid: string; contractorId: string | null };
  snapshot: WorkHomeSnapshot;
  isHq: boolean;
  children?: ReactNode;
}) {
  const [day, setDay] = useState(localDay),
    [month, setMonth] = useState(() => localDay().slice(0, 7));
  const [loaded, setLoaded] = useState<{ key: string; data: DashboardRead } | null>(null),
    [busy, setBusy] = useState(false),
    [refresh, setRefresh] = useState(0);
  const lastHandledRefresh = useRef(0);
  useEffect(() => {
    const refreshTbm = () => setRefresh(value => value + 1);
    window.addEventListener(TBM_DATA_CHANGED_EVENT, refreshTbm);
    return () => window.removeEventListener(TBM_DATA_CHANGED_EVENT, refreshTbm);
  }, []);
  const revealWorkspace = useWorkspaceReady();
  const [settled, setSettled] = useState(false),
    [boardSettled, setBoardSettled] = useState(false);
  const acceptBoardSettled = useCallback(() => setBoardSettled(true), []);
  useEffect(() => {
    // Entry authority is already established; slow cards must not hide the entire workspace.
    revealWorkspace();
  }, [settled, boardSettled, snapshot.state, revealWorkspace]);
  const featureKey = [...featureIds].sort().join('|');
  const scopeKey = JSON.stringify(scope);
  const input = useMemo(
    () => ({
      uid,
      scope: JSON.parse(scopeKey) as ResourceScope,
      day,
      month,
      // Accident counts come from the same site-scoped stored summary as the other annual cards.
      features: featureKey.split('|').filter(id => id !== 'tbm'),
    }),
    [uid, scopeKey, day, month, featureKey],
  );
  const key = JSON.stringify(input);
  const value = loaded?.key === key ? loaded.data : null;
  const allowed = (id: string) => featureIds.includes(id);
  const open = (id: string) => {
    if (allowed(id)) onOpen(id);
  };
  useEffect(() => {
    let active = true;
    const force = refresh > lastHandledRefresh.current;
    lastHandledRefresh.current = refresh;
    setBusy(true);
    void loadDashboard(input, force, summary => {
      if (active) {
        setLoaded({ key: JSON.stringify(input), data: { tbm: null, work: null, inspection: null,
          victimTotal: null, risk: summary.risk, operations: summary, errors: [], at: Date.now() } });
        setSettled(true);
      }
    })
      .then((data) => {
        if (active) setLoaded({ key: JSON.stringify(input), data });
      })
      // A transient refresh failure retains the snapshot; auth failures must immediately clear it.
      .catch((error) => {
        if (active && authorizationFailure(error)) setLoaded(null);
      })
      .finally(() => {
        if (active) {
          setBusy(false);
          setSettled(true);
        }
      });
    return () => {
      active = false;
    };
  }, [input, refresh]);
  useEffect(() => {
    let previousDay = localDay();
    const timer = window.setInterval(() => {
      const today = localDay();
      if (document.visibilityState === 'visible' && previousDay !== today) {
        setDay((selected) => (selected === previousDay ? today : selected));
        setMonth((selected) =>
          selected === previousDay.slice(0, 7) ? today.slice(0, 7) : selected,
        );
        previousDay = today;
      }
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  const [page, setPage] = useState(0);
  const [managementTab, setManagementTab] = useState(false);
  const [managementResult, setManagementResult] = useState<{key:string; data:Awaited<ReturnType<typeof readDashboardManagement>>} | null>(null);
  const management = managementResult?.key === key ? managementResult.data : null;
  useEffect(() => {
    if (!managementTab) return;
    let active = true;
    void readDashboardManagement(input.scope,input.month,input.features).then(data=>{
      if(active)setManagementResult({key:JSON.stringify(input),data});
    }).catch(()=>{if(active)setManagementResult(null);});
    return ()=>{active=false;};
  },[managementTab,input,refresh]);
  const work = value?.work;
  const daily = dashboardWorkRows(work?.rows ?? [], day);
  const filtered = daily;
  const pages = Math.max(1, Math.ceil(filtered.length / 3));
  const currentPage = Math.min(page, pages - 1);
  const rows = filtered.slice(currentPage * 3, currentPage * 3 + 3);
  const inspection = value?.operations?.inspection;
  const visitWork = (item: (typeof daily)[number], stage: DashboardWorkSelection['stage']) => {
    if (onWork) onWork({ row: item.row, day, stage }); else open('work-permit');
  };
  const link = (id: string, label: string) => <button type="button" className="dc-link" disabled={!allowed(id)} onClick={() => open(id)}>{label}<ArrowUpRight size={14}/></button>;
  const monthPeriod={from:`${month}-01`,to:new Date(Date.UTC(Number(month.slice(0,4)),Number(month.slice(5)),0)).toISOString().slice(0,10)};
  const pair = (id: string, label: string, count: ReactNode) => <button type="button" className="dc-pair" disabled={!allowed(id)} onClick={() => open(id)}><span>{label}</span><b>{count}</b></button>;
  const riskMetric = (count: number | undefined, complete: boolean | undefined) =>
    complete === true ? number(count) : count != null && count > 0 ? `${number(count)}+` : '—';
  return <section className="home-dashboard dc-dashboard" data-workspace-dashboard="ledger" aria-busy={busy}>
    <HubHeader title="대시보드" pill={<div className="home-tools">{value?.errors?.length ? <span role="alert">일부 조회 실패 · 다시 시도해 주세요</span> : value?.operations?.refreshPending ? <span role="status">집계 갱신 중</span> : null}<span>{day.replaceAll('-', '.')}</span><Button variant="quiet" size="compact" icon={<RefreshCw size={16}/>} aria-label="대시보드 새로고침" disabled={busy} onClick={() => setRefresh(n => n + 1)}/></div>}/>
    <div className="home-layout"><div className="home-main-grid">
      {'siteId' in scope && <ActivityIndexPanel siteId={scope.siteId} year={Number(month.slice(0,4))} refresh={refresh} reader={safetyProgramActivityReader}/>}
      <section className="home-panel dc-inspection"><header><h2>점검 현황</h2><small>{month.replace('-','년 ')}월</small></header><div className="dc-inspection-metrics">
        {([['점검',inspection?.monthly,'건'],['조치',inspection?.currentMonthCompleted,'건'],['미조치',inspection?.currentMonthUnresolved,'건'],['조치율',inspection?.currentMonthRate,'%']] as const).map(([label,count,unit])=><button type="button" key={label} disabled={!allowed('inspection-reporting')} onClick={()=>label==='점검'?onInspection?.(monthPeriod):onFindings?.({period:monthPeriod,...(label==='조치'?{status:'complete' as const}:label==='미조치'?{status:'not-completed' as const}:{})})} title={label==='조치율'?'이번 달 발생 지적 중 조치 완료 비율':undefined}><span>{label}</span><strong>{number(count)}<small>{count==null?'':unit}</small></strong></button>)}
        </div><footer className="dc-foot"><span>지적 {number(inspection?.currentMonthFindings)}건</span><button type="button" className="dc-link" disabled={!allowed('inspection-reporting')} onClick={()=>onFindings?.({period:{from:'1900-01-01',to:new Date(Date.UTC(Number(month.slice(0,4)),Number(month.slice(5))-1,0)).toISOString().slice(0,10)},status:'not-completed'})}>이월 미조치 {number(inspection?.carriedUnresolved)}건<ArrowUpRight size={14}/></button></footer></section>
    <section className="home-panel dc-work"><header><h2>오늘 비일상작업 <span className="dc-work-total">{work?.complete?daily.length:'—'}건</span></h2>{link('work-permit','전체 대장')}</header>
      <table><thead><tr><th>업체 / 작업명</th><th>허가서</th><th>TBM·서명</th><th>점검</th><th>종료</th></tr></thead><tbody>
        {rows.map(item => <tr key={item.row.workId}><td><button className="dc-workname" type="button" onClick={() => visitWork(item,'permit')}>{item.row.title}</button><small title={`${item.row.contractorName} · ${item.row.area}`}>{item.row.contractorName} · {item.row.area}</small></td>{item.stages.map(stage => <td key={stage.id}><button type="button" className={`dc-state ${stage.tone}`} onClick={() => visitWork(item,stage.id)}>{stage.label}</button></td>)}</tr>)}
        {!rows.length && <tr><td colSpan={5} className="dc-empty">{busy?'불러오는 중':work?.complete?'해당 작업이 없습니다.':'작업 현황을 불러오지 못했습니다.'}</td></tr>}
      </tbody></table><footer className="dc-foot"><span>{work?.complete?`전체 ${filtered.length}건`:'일부 조회 · 전체 집계 불가'}</span><div className="dc-pager"><button type="button" aria-label="이전 작업" disabled={currentPage===0} onClick={()=>setPage(currentPage-1)}>‹</button><span>{currentPage+1} / {pages}</span><button type="button" aria-label="다음 작업" disabled={currentPage+1>=pages} onClick={()=>setPage(currentPage+1)}>›</button></div></footer>
    </section>
    <section className="dc-management"><header><h2>관리 현황</h2><small>{managementTab ? month.replace('-','년 ')+'월' : month.slice(0,4)+'년 누계'}</small><div role="tablist" aria-label="관리 현황 분류">{['안전활동','운영관리'].map((tab,i)=><button type="button" key={tab} role="tab" aria-selected={managementTab===!!i} onClick={()=>setManagementTab(!!i)}>{tab}</button>)}</div></header>
      <div className="home-period-grid">{!managementTab?<>
        <Panel title="위험성평가" note={`${month.slice(0,4)}년 등록자료 기준`}><div className="dc-risk-summary">
          {([
            ['전체 평가',value?.risk?.annualItemCount,value?.risk?.annualItemCountComplete],
            ['감소대책 수립',value?.risk?.annualReductionPlannedCount,value?.risk?.annualReductionPlannedComplete],
            ['조치결과 등록',value?.risk?.annualActionResultCount,value?.risk?.annualActionResultComplete],
          ] as const).map(([label,count,complete],index)=>{
            const registeredCount = index < 2;
            const display = registeredCount ? number(value?.risk?.complete === true ? count : null) : riskMetric(count,complete);
            return <button key={label} type="button" disabled={!allowed('regular-risk')} onClick={()=>open('regular-risk')} title={registeredCount?'올해로 확인된 등록 평가항목 기준입니다. 연도 미확인·미이관 항목은 제외합니다.':complete===true?'올해 조치결과 등록 건수':'조치 근거가 확인된 건수입니다. 미확인 결과는 포함하지 않습니다.'}><span>{label}</span><strong>{display}<small>{display==='—'?'':'건'}</small></strong></button>;
          })}
        </div></Panel>
        <Panel title="교육 · 비상훈련"><div className="dc-training-summary">
          <button type="button" disabled={!allowed('safety-training')} onClick={()=>open('safety-training')}><span>교육</span><strong>{number(value?.operations?.education?.annualTotal)}<small>건</small></strong><em>누적 참석 <b>{number(value?.operations?.education?.annualAttendance)}</b>명{value?.operations?.education?.annualDraft ? ` · 작성 중 ${number(value.operations.education.annualDraft)}건` : ''}</em></button>
          <button type="button" disabled={!allowed('evacuation-drill')} onClick={()=>open('evacuation-drill')}><span>훈련</span><strong>{number(value?.operations?.drill?.annualCompleted)}<small>회 실시</small></strong><em>{month.slice(0,4)}년 누계</em></button>
        </div></Panel>
        <Panel title="안전사고 현황" note="올해 누계"><div className="dc-accident-summary">{(['industrial','minor','property','total'] as const).map((category,index)=><button key={category} type="button" disabled={!allowed('incident')} onClick={()=>open('incident')} aria-label={`${['산재','경미상','물적피해','합계'][index]} 현황`}><span>{['산재','경미상','물적피해','합계'][index]}</span><strong>{value?.operations?.accident?.[category] ?? '—'}<small>건</small></strong></button>)}</div></Panel>
      </>:<>
        <Panel title="MSDS" note="현재"><div className="dc-metrics">{pair('chemical-msds-management','대장 등록',number(management?.msds?.total))}{pair('chemical-msds-management','분석 미완료',number(management?.msds?.incomplete))}</div></Panel>
        <Panel title="안전관리비" note="이달"><div className="dc-metrics">{pair('safety-cost','집행액',management?.budget?number(management.budget.amount)+'원':'—')}{pair('safety-cost','집행 내역',number(management?.budget?.count))}</div></Panel>
        <Panel title="회의 · 신고·건의" note="이달"><div className="dc-metrics">{pair('meeting-minutes','회의 등록',number(management?.meetings?.total))}<button type="button" className="dc-link" onClick={onReport}>신고·건의<ArrowUpRight size={14}/></button></div></Panel>
      </>}</div>
    </section></div>
    <aside className="home-side-grid">
      <HomeBriefing siteId={'siteId' in scope?scope.siteId:null} authority={authority} owner={owner} onSettled={acceptBoardSettled}/>
      {'siteId' in scope&&<DashboardCalendar siteId={scope.siteId} day={day} enabled={allowed('meeting-minutes')} refresh={refresh} onOpen={()=>open('meeting-minutes')}/>}
    </aside></div>
  </section>;
}
